"use client";

import React, { useEffect, useRef, useState } from "react";
import { CircleCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const pillars = [{
  title: "Compassionate Care",
  description: "Our team treats every pet like family, ensuring a stress-free and gentle experience."
}, {
  title: "Modern Veterinary Science",
  description: "Utilizing the latest medical advancements to provide precise diagnostics and treatments."
}, {
  title: "Urban Oasis Comfort",
  description: "A serene environment designed to keep both pets and their owners calm and relaxed."
}];
export default function TheHeartsBehindTheCare() {
  const {
    ref: leftRef,
    isVisible: isLeftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: isRightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="245071" id="the-hearts-behind-the-care" className="py-20 md:py-32 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-12 items-center">
          
          {}
          <div ref={leftRef} className={`relative w-full max-w-xl mx-auto lg:max-w-none transition-all duration-1000 ease-out ${isLeftVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            <div className="relative pr-8 pb-8 md:pr-12 md:pb-12">
              {}
              <div className="rounded-3xl overflow-hidden shadow-xl aspect-[4/3] md:aspect-[4/4] bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687484/img/vet_examining_happy_dog.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687484/img/vet_examining_happy_dog.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687484/img/vet_examining_happy_dog.png-wvc-sizes" alt="Veterinarian examining a happy dog in a modern clinic" className="w-full h-full object-cover" loading="lazy" />
              </div>
              
              {}
              <div className="absolute bottom-0 right-0 w-[65%] md:w-[55%] aspect-square rounded-3xl overflow-hidden shadow-2xl border-8 border-background bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687484/img/cat_relaxing_in_clinic.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687484/img/cat_relaxing_in_clinic.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687484/img/cat_relaxing_in_clinic.png-wvc-sizes" alt="Calm cat relaxing in the serene clinic environment" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`flex flex-col gap-8 transition-all duration-1000 delay-200 ease-out ${isRightVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="flex flex-col gap-4">
              <Badge variant="secondary" className="w-fit text-sm px-3 py-1 bg-accent text-accent-foreground hover:bg-accent/80 rounded-md">
                The Hearts Behind the Care
              </Badge>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground leading-tight tracking-tight">
                Where Empathy Meets Veterinary Excellence
              </h2>
              <p className="text-lg text-muted-foreground font-default leading-relaxed">
                At City Pet Care, we believe that healing begins with a gentle touch. Our urban oasis combines high-end medical precision with a deep understanding of the bond between you and your pet.
              </p>
            </div>

            <div className="flex flex-col gap-6 mt-2">
              {pillars.map((pillar, index) => <div key={index} data-index={index} className="flex items-start gap-4">
                  <div className="mt-1 bg-accent p-1 rounded-lg shrink-0">
                    <CircleCheck className="w-6 h-6 text-accent-foreground" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <h3 className="text-xl font-semibold font-default text-foreground">
                      {pillar.title}
                    </h3>
                    <p className="text-muted-foreground font-default">
                      {pillar.description}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="pt-4">
              <Button asChild size="lg" className="font-default text-base px-8 rounded-lg bg-primary hover:opacity-90 transition-opacity shadow-md">
                <Link to="/OurMedicalTeamPage">
                  Meet Our Specialists
                </Link>
              </Button>
            </div>
          </div>

        </div>
      </div>
    </section>;
}